import React, { useState } from "react";
import styles from "./GroundednessBadge.module.css";
import { GroundednessData, GroundednessLevel } from "../../api";

type GroundednessBadgeProps = {
  groundedness?: GroundednessData | null;
  className?: string;
};

const CONFIDENCE_CONFIG: Record<
  GroundednessLevel,
  { label: string; description: string }
> = {
  high: {
    label: "High Confidence",
    description:
      "This answer is well-supported by the source documents with direct citations.",
  },
  medium: {
    label: "Medium Confidence",
    description:
      "This answer is mostly supported but may include some reasonable inferences from the sources.",
  },
  low: {
    label: "Low Confidence",
    description:
      "This answer has limited support from source documents. Please verify with official sources.",
  },
};

/**
 * Displays color-coded confidence badge based on grounding validation.
 * Shows tooltip explaining the confidence level and reasoning.
 */
export const GroundednessBadge: React.FC<GroundednessBadgeProps> = ({
  groundedness,
  className,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!groundedness) return null;

  const config = CONFIDENCE_CONFIG[groundedness.confidence];
  const badgeClass = styles[groundedness.confidence];

  return (
    <div className={`${styles.badgeContainer} ${className || ""}`}>
      <span
        className={`${styles.badge} ${badgeClass}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        tabIndex={0}
        role="note"
        aria-describedby="groundedness-tooltip"
      >
        <span className={styles.dot} />
        {config.label}
      </span>

      {showTooltip && (
        <div
          id="groundedness-tooltip"
          className={styles.tooltip}
          role="tooltip"
        >
          <div className={styles.tooltipContent}>
            <strong>{config.label}</strong>
            <p>{config.description}</p>
            {groundedness.reasoning && (
              <p className={styles.reasoning}>{groundedness.reasoning}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
